import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom';
import PortfolioDashboard from '../components/Consultant/PortfolioDashboard';
import ConsultantClientDetail from '../components/Consultant/ConsultantClientDetail';
import AddConsultantClientWizard from '../components/Consultant/AddConsultantClientWizard';
import OnboardExistingClientWizard from '../components/Consultant/OnboardExistingClientWizard';
import AmendConsultantClientModal from '../components/Consultant/AmendConsultantClientModal';
import { Actions as CONSULTANT_ACTIONS } from '../store/actions/consultantClientsActions';
import { toggleAlertMessage } from '../store/action';
import { filterConsultantClients } from '../components/utils/consultantClientUtils';

const ConsultantPortfolio = ({
  consultantParentId,
  clients,
  renewals,
  schedules,
  masterPerReportFeeVal,
  pendingContracts,
  loading,
  actionLoading,
  getConsultantClients,
  getConsultantRenewals,
  getConsultantSchedules,
  hydratePendingContracts,
  activateConsultantClient,
  resendConsultantClientContract,
  voidConsultantClientContract,
  clearPendingContract,
  showAlert,
}) => {
  const history = useHistory();
  const location = useLocation();
  const [showAddWizard, setShowAddWizard] = useState(false);
  const [showOnboardWizard, setShowOnboardWizard] = useState(false);
  const [amendTarget, setAmendTarget] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('add') === '1') {
      setShowAddWizard(true);
      history.replace('/consultant/portfolio');
    }
  }, [location.search, history]);

  useEffect(() => {
    hydratePendingContracts();
    getConsultantClients(true);
    getConsultantRenewals();
    getConsultantSchedules();
  }, [getConsultantClients, getConsultantRenewals, getConsultantSchedules, hydratePendingContracts]);

  const clientRows = useMemo(
    () => filterConsultantClients(clients, consultantParentId),
    [clients, consultantParentId],
  );

  const handleRefresh = () => {
    getConsultantClients(true);
    getConsultantRenewals();
    getConsultantSchedules();
  };

  const handleActivate = async (orgId) => {
    const result = await activateConsultantClient(orgId);
    showAlert(true, result.ok ? 'Client activated.' : result.message, result.ok ? 'success' : 'error', 5000);
  };

  const handleResend = async (orgId, contractId) => {
    const result = await resendConsultantClientContract(contractId, { rotateToken: true });
    if (result.ok) {
      showAlert(true, 'Invitation resent.', 'success', 4000);
      if (result.data?.agreementUrl) window.open(result.data.agreementUrl, '_blank', 'noopener');
    } else {
      showAlert(true, result.message, 'error', 5000);
    }
  };

  const handleVoid = async (orgId, contractId) => {
    const reason = window.prompt('Reason for voiding this invitation (optional):') || 'Voided by consultant';
    const result = await voidConsultantClientContract(contractId, { reason });
    if (result.ok) {
      clearPendingContract(orgId);
      showAlert(true, 'Invitation voided.', 'success', 4000);
      handleRefresh();
    } else {
      showAlert(true, result.message, 'error', 5000);
    }
  };

  return (
    <div className='p-6'>
      <Switch>
        <Route exact path='/consultant'>
          <Redirect to='/consultant/portfolio' />
        </Route>
        <Route path='/consultant/client/:id'>
          <ConsultantClientDetail
            clients={clientRows}
            renewals={renewals}
            schedules={schedules}
            masterPerReportFeeVal={masterPerReportFeeVal}
            onBack={() => history.push('/consultant/portfolio')}
          />
        </Route>
        <Route path='/consultant/portfolio'>
          <PortfolioDashboard
            clients={clients}
            renewals={renewals}
            schedules={schedules}
            masterPerReportFeeVal={masterPerReportFeeVal}
            pendingContracts={pendingContracts}
            consultantParentId={consultantParentId}
            loading={!!loading.clients}
            actionLoading={actionLoading}
            onAddClient={() => setShowAddWizard(true)}
            onOnboardExisting={() => setShowOnboardWizard(true)}
            onActivate={handleActivate}
            onResend={handleResend}
            onVoid={handleVoid}
            onAmend={(client) => setAmendTarget(client)}
            onRefresh={handleRefresh}
          />
        </Route>
      </Switch>

      {showAddWizard && (
        <AddConsultantClientWizard
          open={showAddWizard}
          onClose={() => setShowAddWizard(false)}
          onSuccess={() => {
            setShowAddWizard(false);
            handleRefresh();
          }}
        />
      )}

      {showOnboardWizard && (
        <OnboardExistingClientWizard
          open={showOnboardWizard}
          onClose={() => setShowOnboardWizard(false)}
          onSuccess={() => {
            setShowOnboardWizard(false);
            handleRefresh();
          }}
        />
      )}

      {amendTarget && (
        <AmendConsultantClientModal
          open={!!amendTarget}
          client={amendTarget}
          onClose={() => setAmendTarget(null)}
          onSuccess={() => {
            setAmendTarget(null);
            handleRefresh();
          }}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  consultantParentId: state.profile?.details?.consultantAdmin,
  clients: state.consultantClients?.clients || [],
  renewals: state.consultantClients?.renewals || [],
  schedules: state.consultantClients?.schedules || [],
  masterPerReportFeeVal: state.consultantClients?.masterPerReportFeeVal,
  pendingContracts: state.consultantClients?.pendingContracts || {},
  loading: state.consultantClients?.loading || {},
  actionLoading: state.consultantClients?.loading || {},
});

const mapDispatchToProps = (dispatch) => ({
  getConsultantClients: (includeAll) => dispatch(CONSULTANT_ACTIONS.getConsultantClients(includeAll)),
  getConsultantRenewals: () => dispatch(CONSULTANT_ACTIONS.getConsultantRenewals()),
  getConsultantSchedules: () => dispatch(CONSULTANT_ACTIONS.getConsultantSchedules()),
  hydratePendingContracts: () => dispatch(CONSULTANT_ACTIONS.hydratePendingContracts()),
  activateConsultantClient: (orgId) => dispatch(CONSULTANT_ACTIONS.activateConsultantClient(orgId)),
  resendConsultantClientContract: (contractId, body) =>
    dispatch(CONSULTANT_ACTIONS.resendConsultantClientContract(contractId, body)),
  voidConsultantClientContract: (contractId, body) =>
    dispatch(CONSULTANT_ACTIONS.voidConsultantClientContract(contractId, body)),
  clearPendingContract: (orgId) => dispatch(CONSULTANT_ACTIONS.clearPendingContract(orgId)),
  showAlert: (status, message, severity, hideDuration) =>
    dispatch(toggleAlertMessage(status, message, severity, hideDuration)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ConsultantPortfolio);
